/**
 * The offer ladder (Free → AI-Ready Day → Mastermind, with the Done-for-you
 * month beside the event): each rung's price, what it is, the share of
 * the rung before that should step up, and live counts where the platform
 * knows them. Prices and targets are edited in place.
 *
 * Data: GET/PATCH /api/command/offers (api/src/routes/command.ts).
 */
import { useEffect, useRef, useState } from "react";
import { Layers } from "lucide-react";
import { Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Empty, Section, dotClass, num, usd } from "@/features/command/ui";

interface Offer {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  price_usd: string | number;
  recurring: string | null;
  feeds_from: string | null;
  target_rate: string | number | null;
  plan_code: string | null;
  color: string | null;
  position: number;
  live_count: number | null;
  live_paying: number | null;
}

/** "DIY — $397" → "DIY" (the price sits next to it, and stays right when it's edited). */
function rungName(name: string): string {
  const base = name.replace(/\s*[—–-]\s*\$[\d.,]+\s*[kKmM]?\s*$/, "").trim();
  return (base || name).replace(/\s+[—–]\s+/g, " · ");
}

/** "Free (Pro, quantity-capped)" → "Free", for "4% of Free". */
function shortName(name: string): string {
  return rungName(name).replace(/\s*\([^)]*\)\s*$/, "") || rungName(name);
}

/** Plain punctuation: no long dashes in what people read. */
function plain(text: string | null): string {
  return String(text ?? "").replace(/\s*—\s*/g, ", ").replace(/\s+–\s+/g, ", ");
}

const PER: Record<string, string> = { month: "/mo", monthly: "/mo", year: "/yr", yearly: "/yr", annual: "/yr", week: "/wk" };

export function OfferLadder() {
  const toast = useToast();
  const [offers, setOffers] = useState<Offer[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ offers: Offer[] }>("/command/offers")
      .then((d) => { if (!cancelled) setOffers([...d.offers].sort((a, b) => a.position - b.position)); })
      .catch((err) => {
        if (cancelled) return;
        setOffers([]);
        toast.error("Couldn't load the offer ladder", err instanceof ApiError ? err.message : undefined);
      });
    return () => { cancelled = true; };
  }, [toast]);

  const save = async (o: Offer, patch: { price_usd?: number; target_rate?: number }, what: string) => {
    const before = offers;
    setOffers((list) => list?.map((x) => (x.id === o.id ? { ...x, ...patch } : x)) ?? list);
    try {
      const r = await api.patch<{ offer: Offer }>(`/command/offers/${o.id}`, patch);
      setOffers((list) => list?.map((x) => (x.id === o.id ? { ...x, ...r.offer, live_count: x.live_count, live_paying: x.live_paying } : x)) ?? list);
      toast.success(`${rungName(o.name)}: ${what} saved`);
    } catch (err) {
      setOffers(before);
      toast.error("Couldn't save that", err instanceof ApiError ? err.message : undefined);
    }
  };

  const byId = new Map((offers ?? []).map((o) => [o.id, o]));

  return (
    <Section title="Offer ladder" icon={Layers} count={offers?.length} action={<span className="hidden text-[11px] text-zinc-500 sm:inline">click a price or target to change it</span>}>
      {!offers ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : offers.length === 0 ? (
        <Empty icon={Layers} title="No offers yet">The ladder from Free up to the mastermind shows here once it's set up.</Empty>
      ) : (
        <ol className="relative space-y-1">
          {offers.map((o, i) => {
            const price = Number(o.price_usd) || 0;
            const rate = o.target_rate == null ? null : Number(o.target_rate);
            const from = o.feeds_from ? byId.get(o.feeds_from) : null;
            const last = i === offers.length - 1;
            return (
              <li key={o.id} className="relative flex gap-3 pb-3">
                {/* The rail between rungs */}
                {!last && <span className="absolute -bottom-1 left-[7px] top-6 w-px bg-zinc-700/80" aria-hidden="true" />}
                <span className={cn("relative mt-1.5 h-[15px] w-[15px] shrink-0 rounded-full ring-4 ring-zinc-950/80", dotClass(o.color))} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-sm font-semibold text-zinc-100">{rungName(o.name)}</span>
                    <InlineNumber
                      value={price}
                      label={`${rungName(o.name)} price in dollars`}
                      display={price === 0 ? "Free" : `${usd(price, { cents: price % 1 !== 0 })}${o.recurring ? PER[o.recurring] ?? `/${o.recurring}` : ""}`}
                      className="text-base font-semibold tabular-nums text-zinc-50"
                      prefix="$"
                      onSave={(v) => save(o, { price_usd: v }, "price")}
                    />
                  </div>
                  {o.description && <p className="mt-0.5 text-xs leading-relaxed text-zinc-400">{plain(o.description)}</p>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
                    {from ? (
                      <span className="inline-flex items-center gap-1">
                        Target
                        <InlineNumber
                          value={rate == null ? 0 : Math.round(rate * 10000) / 100}
                          label={`${rungName(o.name)} target percent`}
                          display={rate == null ? "not set" : `${Math.round(rate * 1000) / 10}%`}
                          className="font-semibold tabular-nums text-indigo-300"
                          suffix="%"
                          onSave={(v) => save(o, { target_rate: Math.max(0, Math.min(100, v)) / 100 }, "target")}
                        />
                        of {shortName(from.name)}
                      </span>
                    ) : (
                      <span>Where everyone starts</span>
                    )}
                    {o.live_count != null && (
                      <span className="tabular-nums">
                        <span className="font-semibold text-zinc-300">{num(o.live_count)}</span> on it now
                        {o.live_paying != null && <> · <span className={cn("font-semibold", o.live_paying > 0 ? "text-emerald-400" : "text-zinc-300")}>{num(o.live_paying)}</span> paying</>}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Section>
  );
}

/** A number you click to change: Enter or click away saves, Escape cancels. */
function InlineNumber({ value, display, onSave, className, label, prefix, suffix }: {
  value: number;
  display: string;
  onSave: (v: number) => Promise<void> | void;
  className?: string;
  label: string;
  prefix?: string;
  suffix?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) ref.current?.select(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const v = Number(draft.replace(/[$,%\s]/g, ""));
    if (draft.trim() === "" || !Number.isFinite(v) || v < 0 || v === value) return;
    void onSave(v);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-md border border-indigo-500/50 bg-zinc-900 px-1.5">
        {prefix && <span className="text-xs text-zinc-500">{prefix}</span>}
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          aria-label={label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          className="w-20 bg-transparent py-0.5 text-right text-sm text-zinc-100 outline-none"
        />
        {suffix && <span className="text-xs text-zinc-500">{suffix}</span>}
      </span>
    );
  }
  return (
    <button
      type="button"
      title={`Change ${label}`}
      onClick={() => { setDraft(String(value)); setEditing(true); }}
      className={cn("cursor-pointer rounded-md px-1 decoration-dotted underline-offset-4 hover:bg-zinc-800/60 hover:underline", className)}
    >
      {display}
    </button>
  );
}
